/**
 * LocationSettings Component
 * Allows users to set their location for accurate sundown-to-sundown calculations
 */

import { useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LocationSettings({ onLocationUpdate, compact = false }) {
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [zipCode, setZipCode] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [sunsetInfo, setSunsetInfo] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Fetch current location
  const fetchLocation = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/user/location`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setLocation(response.data.location);
      
      // Also fetch sunset info
      try {
        const sunsetResponse = await axios.get(`${API}/user/location/sunset-info`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSunsetInfo(sunsetResponse.data);
      } catch (e) {
        console.warn("Sunset info unavailable");
      }
    } catch (err) {
      console.error("Failed to fetch location:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocation();
  }, []);

  // Handle zip code submission
  const handleZipSubmit = async (e) => {
    e.preventDefault();
    if (!zipCode.trim()) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/user/location/from-zip`,
        { zip_code: zipCode.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setLocation(response.data.location);
      setSuccess(response.data.message);
      setShowForm(false);

      // Notify parent component
      if (onLocationUpdate) {
        onLocationUpdate(response.data.location);
      }

      // Refresh sunset info
      fetchLocation();
    } catch (err) {
      console.error("Failed to set location:", err);
      setError(
        err.response?.data?.detail ||
          "Failed to set location. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  // Request browser geolocation
  const handleUseDeviceLocation = () => {
    if (!("geolocation" in navigator)) {
      setError("Geolocation is not supported by your browser");
      return;
    }

    setSaving(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const token = localStorage.getItem("token");
          
          // Estimate timezone from longitude
          const tzOffset = Math.round(position.coords.longitude / 15);

          await axios.put(
            `${API}/user/location`,
            {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              timezone_offset_hours: tzOffset,
              city: "Your Location",
              state: "Device GPS",
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          setSuccess("Location set from your device GPS");
          fetchLocation();

          if (onLocationUpdate) {
            onLocationUpdate({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              timezone_offset_hours: tzOffset,
            });
          }
        } catch (err) {
          setError("Failed to save device location");
        } finally {
          setSaving(false);
        }
      },
      (err) => {
        setSaving(false);
        setError("Location access denied. Please enter your zip code.");
      }
    );
  };

  if (loading) {
    return (
      <div
        style={{
          padding: "15px",
          background: "#1a1a2e",
          borderRadius: "10px",
          textAlign: "center",
        }}
      >
        <p style={{ color: "#aaa", margin: 0 }}>Loading location...</p>
      </div>
    );
  }

  // Compact view
  if (compact && !showForm) {
    return (
      <div
        data-testid="location-settings-compact"
        style={{
          padding: "12px 15px",
          background: "linear-gradient(135deg, #1a2a1a 0%, #0a1a0a 100%)",
          border: "1px solid #90EE9040",
          borderRadius: "8px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <div style={{ color: "#90EE90", fontSize: "0.8rem", fontWeight: "bold" }}>
            📍 {location?.city || "Location"}, {location?.state || ""}
          </div>
          {sunsetInfo && (
            <div style={{ color: "#888", fontSize: "0.7rem", marginTop: "3px" }}>
              Local: {sunsetInfo.local_time?.formatted} •{" "}
              {sunsetInfo.sunset?.is_after_sunset
                ? "After Sunset"
                : `Sunset ~${sunsetInfo.sunset?.estimated_time}`}
            </div>
          )}
        </div>
        <button
          onClick={() => setShowForm(true)}
          style={{
            background: "transparent",
            border: "1px solid #90EE90",
            borderRadius: "4px",
            color: "#90EE90",
            padding: "4px 8px",
            fontSize: "0.7rem",
            cursor: "pointer",
          }}
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="location-settings"
      style={{
        background: "linear-gradient(135deg, #1a2a1a 0%, #0a1a0a 100%)",
        border: "2px solid #90EE90",
        borderRadius: "12px",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "linear-gradient(90deg, #90EE9020 0%, transparent 100%)",
          padding: "15px 20px",
          borderBottom: "1px solid #90EE9040",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h3 style={{ color: "#90EE90", margin: 0, fontSize: "1rem" }}>
            📍 Location Settings
          </h3>
          <p style={{ color: "#aaa", margin: "3px 0 0", fontSize: "0.75rem" }}>
            For accurate sundown-to-sundown calculations
          </p>
        </div>
        {compact && (
          <button
            onClick={() => setShowForm(false)}
            style={{
              background: "transparent",
              border: "none",
              color: "#90EE90",
              cursor: "pointer",
              fontSize: "1.2rem",
            }}
          >
            ×
          </button>
        )}
      </div>

      <div style={{ padding: "20px" }}>
        {/* Current Location Display */}
        {location && (
          <div
            style={{
              background: "#0a0a0a",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "15px",
              border: "1px solid #333",
            }}
          >
            <div style={{ color: "#888", fontSize: "0.75rem", marginBottom: "5px" }}>
              CURRENT LOCATION
            </div>
            <div style={{ color: "#fff", fontSize: "1rem", fontWeight: "bold" }}>
              {location.city || "Unknown"}, {location.state || ""}
            </div>
            {location.zip_code && (
              <div style={{ color: "#aaa", fontSize: "0.85rem" }}>
                Zip: {location.zip_code}
              </div>
            )}
            <div style={{ color: "#87CEEB", fontSize: "0.8rem", marginTop: "5px" }}>
              Timezone: UTC{location.timezone_offset_hours >= 0 ? "+" : ""}
              {location.timezone_offset_hours}
            </div>
            {location.is_default && (
              <div
                style={{
                  color: "#FFD700",
                  fontSize: "0.75rem",
                  marginTop: "8px",
                  fontStyle: "italic",
                }}
              >
                Using St. Louis/Cahokia as default. Set your location for accurate timing.
              </div>
            )}
          </div>
        )}

        {/* Sunset Info */}
        {sunsetInfo && (
          <div
            style={{
              background: sunsetInfo.sunset?.is_after_sunset
                ? "linear-gradient(135deg, #2a1a3a 0%, #1a0a2a 100%)"
                : "linear-gradient(135deg, #3a3a1a 0%, #2a2a0a 100%)",
              borderRadius: "8px",
              padding: "12px 15px",
              marginBottom: "15px",
              border: sunsetInfo.sunset?.is_after_sunset
                ? "1px solid #9370DB"
                : "1px solid #FFD700",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div
                  style={{
                    color: sunsetInfo.sunset?.is_after_sunset ? "#9370DB" : "#FFD700",
                    fontSize: "0.8rem",
                    fontWeight: "bold",
                  }}
                >
                  {sunsetInfo.sunset?.is_after_sunset
                    ? "🌙 After Sunset"
                    : "☀️ Before Sunset"}
                </div>
                <div style={{ color: "#ccc", fontSize: "0.75rem", marginTop: "3px" }}>
                  {sunsetInfo.sunset?.biblical_day_status}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ color: "#fff", fontSize: "1rem", fontWeight: "bold" }}>
                  {sunsetInfo.local_time?.formatted}
                </div>
                <div style={{ color: "#888", fontSize: "0.7rem" }}>Local Time</div>
              </div>
            </div>
          </div>
        )}

        {/* Messages */}
        {error && (
          <div
            style={{
              background: "#4d1a1a",
              border: "1px solid #ff6b6b",
              borderRadius: "6px",
              padding: "10px 15px",
              marginBottom: "15px",
              color: "#ff6b6b",
              fontSize: "0.85rem",
            }}
          >
            {error}
          </div>
        )}

        {success && (
          <div
            style={{
              background: "#1a4d1a",
              border: "1px solid #90EE90",
              borderRadius: "6px",
              padding: "10px 15px",
              marginBottom: "15px",
              color: "#90EE90",
              fontSize: "0.85rem",
            }}
          >
            {success}
          </div>
        )}

        {/* Zip Code Form */}
        <form onSubmit={handleZipSubmit}>
          <label
            style={{
              display: "block",
              color: "#aaa",
              fontSize: "0.8rem",
              marginBottom: "8px",
            }}
          >
            Enter your US Zip Code:
          </label>
          <div style={{ display: "flex", gap: "10px" }}>
            <input
              type="text"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
              placeholder="e.g., 62205"
              maxLength={5}
              style={{
                flex: 1,
                padding: "12px 15px",
                background: "#0a0a0a",
                border: "1px solid #333",
                borderRadius: "6px",
                color: "#fff",
                fontSize: "1rem",
              }}
            />
            <button
              type="submit"
              disabled={saving || !zipCode.trim()}
              style={{
                padding: "12px 20px",
                background: saving ? "#666" : "#90EE90",
                border: "none",
                borderRadius: "6px",
                color: "#000",
                fontWeight: "bold",
                cursor: saving ? "not-allowed" : "pointer",
              }}
            >
              {saving ? "..." : "Set"}
            </button>
          </div>
        </form>

        {/* Or Use Device Location */}
        <div style={{ marginTop: "15px", textAlign: "center" }}>
          <span style={{ color: "#666", fontSize: "0.8rem" }}>or</span>
          <button
            onClick={handleUseDeviceLocation}
            disabled={saving}
            style={{
              display: "block",
              width: "100%",
              marginTop: "10px",
              padding: "12px",
              background: "transparent",
              border: "1px solid #87CEEB",
              borderRadius: "6px",
              color: "#87CEEB",
              cursor: saving ? "not-allowed" : "pointer",
            }}
          >
            📱 Use Device Location
          </button>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          padding: "10px 20px",
          background: "#050505",
          borderTop: "1px solid #333",
        }}
      >
        <p style={{ color: "#666", margin: 0, fontSize: "0.7rem" }}>
          Biblical days run from sundown to sundown. Your location ensures
          accurate Sabbath timing.
        </p>
      </div>
    </div>
  );
}

export default LocationSettings;
